import React, { useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import axios from "axios";

import ReactTable from "../../reusableComponents/ReactTable/ReactTable";
import LoaderOverlay from "../Loader";
import { baseUrl } from "../../utils/api/apis";
import { FormatUSD } from "../../utils/string";
import { DatePicker, Switch } from "antd";
import moment from "moment";
import dayjs from "dayjs";

const { RangePicker } = DatePicker;

const SalesSummary = () => {
  const { idToken } = useSelector((state) => state.auth);

  const [reports, setReports] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [loading, setLoading] = useState(false);


  // Server-side pagination params
  const [params, setParams] = useState({
    page: 1,
    page_size: 10,
    start_date: "",
    end_date: "",
    timezone: "BST",
  });

  const isBST = params.timezone === "BST";

  // Columns - Placeholder for Sales Summary
  // TODO: Adjust columns based on actual API response
  const columns = useMemo(
    () => [
      {
        Header: "Date",
        accessor: "timestamp",
        Cell: ({ value }) => {
          if (!value) return "-";
          const m = moment.utc(value);
          const display = isBST ? m.utcOffset("+01:00") : m;
          return display.format("YYYY/MM/DD hh:mm:ss a");
        },
      },
      {
        Header: "Payment Mode",
        accessor: "payment_mode",
        Cell: ({ value }) =>
          value ? (
            <span style={{ textTransform: "capitalize" }}>{value}</span>
          ) : (
            "-"
          ),
      },
      {
        Header: "Sales",
        accessor: "sales",
        Cell: ({ value }) => (value ? FormatUSD(value) : "-"),
      },
      {
        Header: "Count",
        accessor: "count",
        Cell: ({ value }) => (value ? value : "-"),
      },
    ],
    [isBST],
  );

  const fetchSalesSummary = async (currentParams = params) => {
    if (!idToken) return;

    setLoading(true);
    try {
      const config = {
        headers: { Authorization: `Bearer ${idToken}` },
        params: params,
      };

      const response = await axios.get(
        `${baseUrl}payment/admin/sales-report/`,
        config,
      );

      let rows = [];
      let count = 0;

      if (Array.isArray(response.data)) {
        rows = response.data;
        count = response.data.length;
      } else if (Array.isArray(response.data.results)) {
        rows = response.data.results;
        count = response.data.count ?? response.data.results.length;
      } else if (Array.isArray(response.data.data)) {
        rows = response.data.data;
        count = response.data.total ?? response.data.data.length;
      } else {
        rows = [];
        count = 0;
      }

      setReports(rows);
      setTotalCount(count);
    } catch (error) {
      console.error("Error fetching Sales Summary:", error);
    } finally {
      setLoading(false);
    }
  };

  // Fetch whenever page or page_size or dateRange changes
  useEffect(() => {
    fetchSalesSummary(params);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [params]);

  return (
    <div className="table_scroll_wrapper">
      <div className="alltable">
        <div className="table_wrapper">
          {loading && <LoaderOverlay />}

          <div
            className="reports_filters"
            style={{
              marginBottom: 16,
              display: "flex",
              gap: 12,
              alignItems: "center",
            }}
          >
            <span>Date Range:</span>
            <RangePicker
              value={
                params.start_date && params.end_date
                  ? [dayjs(params.start_date), dayjs(params.end_date)]
                  : null
              }
              onChange={(val) => {
                setParams((pre) => ({
                  ...pre,
                  start_date: val && val[0] ? val[0].format("YYYY-MM-DD") : "",
                  end_date: val && val[1] ? val[1].format("YYYY-MM-DD") : "",
                  page: 1,
                }));
              }}
              allowClear
            />
            {/* Timezone toggle */}
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: 8,
                marginLeft: "auto",
                background: "rgba(255,255,255,0.06)",
                border: "1px solid rgba(255,255,255,0.12)",
                borderRadius: 8,
                padding: "4px 12px",
              }}
            >
              <span style={{ fontWeight: 500, fontSize: 13, opacity: 0.8 }}>
                UTC
              </span>
              <Switch
                checked={params.timezone === "BST"}
                onChange={(checked) =>
                  setParams((pre) => ({
                    ...pre,
                    timezone: checked ? "BST" : "UTC",
                    page: 1,
                  }))
                }
                size="small"
              />
              <span
                style={{
                  fontWeight: 600,
                  fontSize: 13,
                  color: isBST ? "#4ade80" : "inherit",
                  transition: "color 0.2s",
                }}
              >
                BST
              </span>
            </div>
          </div>

          <div className="alltable">
            <ReactTable
              columns={columns}
              data={reports}
              serverSide={true}
              params={params}
              setParams={setParams}
              totalCount={totalCount}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default SalesSummary;
